import { isMarkdownName } from "../names.ts";
import { ConflictError, ExistsError, NotFoundError, PermissionError } from "./errors.ts";
import type { DocMeta, ReadResult, Store, WriteOptions, WriteResult } from "./types.ts";

/** The version of a file on disk: when it changed and how long it is. */
function versionOf(file: File): string {
  return `${file.lastModified}:${file.size}`;
}

/**
 * A real folder on disk, through the File System Access API (Chromium).
 * Only Markdown files directly inside the folder are listed.
 */
export class FsaStore implements Store {
  kind = "fsa";
  dir: FileSystemDirectoryHandle;

  constructor(dir: FileSystemDirectoryHandle) {
    this.dir = dir;
  }

  get label(): string {
    return this.dir.name;
  }

  /** Whether this browser can open a folder at all. */
  static supported(): boolean {
    return (
      typeof (globalThis as { showDirectoryPicker?: unknown }).showDirectoryPicker === "function"
    );
  }

  /** Asks the writer to choose a folder. Must run from a click. */
  static async pick(): Promise<FsaStore> {
    const dir = await showDirectoryPicker({ mode: "readwrite", id: "quire" });
    return new FsaStore(dir);
  }

  /**
   * "granted", "prompt" or "denied". Browsers that cannot say are treated as granted.
   */
  async permission(): Promise<PermissionState> {
    if (!this.dir.queryPermission) return "granted";
    return this.dir.queryPermission({ mode: "readwrite" });
  }

  /** Asks again for access. Must run from a click. */
  async requestPermission(): Promise<boolean> {
    if (!this.dir.requestPermission) return true;
    return (await this.dir.requestPermission({ mode: "readwrite" })) === "granted";
  }

  async #ensure() {
    if ((await this.permission()) !== "granted") throw new PermissionError();
  }

  async list(): Promise<DocMeta[]> {
    await this.#ensure();
    const out: DocMeta[] = [];
    for await (const [name, handle] of this.dir.entries()) {
      if (handle.kind !== "file" || !isMarkdownName(name) || name.startsWith(".")) continue;
      const file = await handle.getFile();
      out.push({ path: name, name, updatedAt: file.lastModified, size: file.size });
    }
    return out;
  }

  async read(path: string): Promise<ReadResult> {
    await this.#ensure();
    let handle: FileSystemFileHandle;
    try {
      handle = await this.dir.getFileHandle(path);
    } catch (error) {
      throw this.#translate(error, path);
    }
    const file = await handle.getFile();
    return { text: await file.text(), version: versionOf(file), updatedAt: file.lastModified };
  }

  async readAll(): Promise<(ReadResult & { path: string })[]> {
    const metas = await this.list();
    return Promise.all(metas.map(async (m) => ({ path: m.path, ...(await this.read(m.path)) })));
  }

  async write(path: string, text: string, options: WriteOptions = {}): Promise<WriteResult> {
    await this.#ensure();

    if (options.create || options.expectVersion !== undefined) {
      let current: File | null = null;
      try {
        current = await (await this.dir.getFileHandle(path)).getFile();
      } catch (error) {
        if ((error as Error)?.name !== "NotFoundError") throw this.#translate(error, path);
      }
      if (options.create && current) throw new ExistsError(path);
      if (
        options.expectVersion !== undefined &&
        (!current || versionOf(current) !== options.expectVersion)
      ) {
        throw new ConflictError(path);
      }
    }

    let handle: FileSystemFileHandle;
    try {
      handle = await this.dir.getFileHandle(path, { create: true });
      const writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
    } catch (error) {
      throw this.#translate(error, path);
    }
    const file = await handle.getFile();
    return { version: versionOf(file), updatedAt: file.lastModified };
  }

  async remove(path: string): Promise<void> {
    await this.#ensure();
    try {
      await this.dir.removeEntry(path);
    } catch (error) {
      throw this.#translate(error, path);
    }
  }

  /** The `images` folder beside the documents; made when something is saved into it. */
  async #images(create: boolean): Promise<FileSystemDirectoryHandle | null> {
    await this.#ensure();
    try {
      return await this.dir.getDirectoryHandle("images", { create });
    } catch (error) {
      if (!create && (error as Error)?.name === "NotFoundError") return null;
      throw this.#translate(error, "images");
    }
  }

  async writeAsset(path: string, data: Blob): Promise<void> {
    const dir = await this.#images(true);
    try {
      const handle = await dir!.getFileHandle(path.replace(/^images\//, ""), { create: true });
      const writable = await handle.createWritable();
      await writable.write(data);
      await writable.close();
    } catch (error) {
      throw this.#translate(error, path);
    }
  }

  async readAsset(path: string): Promise<Blob | null> {
    const dir = await this.#images(false);
    if (!dir) return null;
    try {
      return await (await dir.getFileHandle(path.replace(/^images\//, ""))).getFile();
    } catch (error) {
      if ((error as Error)?.name === "NotFoundError") return null;
      throw this.#translate(error, path);
    }
  }

  async listAssets(): Promise<string[]> {
    const dir = await this.#images(false);
    const out: string[] = [];
    if (dir)
      for await (const [name, handle] of dir.entries())
        if (handle.kind === "file") out.push(`images/${name}`);
    return out;
  }

  async removeAsset(path: string): Promise<void> {
    const dir = await this.#images(false);
    if (!dir) return;
    try {
      await dir.removeEntry(path.replace(/^images\//, ""));
    } catch (error) {
      if ((error as Error)?.name !== "NotFoundError") throw this.#translate(error, path);
    }
  }

  #translate(error: unknown, path: string): unknown {
    const name = (error as Error | undefined)?.name;
    if (name === "NotFoundError") return new NotFoundError(path);
    if (name === "NotAllowedError" || name === "SecurityError") {
      return new PermissionError();
    }
    return error;
  }
}
